/**
 * **How the client actually executed a plan**, for the three plan cards: one that is ending, one
 * whose schedule ran out, one that closed itself.
 *
 * Owner, 24 September 2026: a plan card that says only the plan's name asks the trainer to open
 * the plan to decide anything. `GET /plan-assignment/{id}/execution-summary` already carries the
 * answer — every cycle's slots with their status, the counts, the tonnage — so the card states it:
 * sessions done of prescribed, missed, attendance, tonnage, records, and a strip week by week.
 *
 * ── The counts are the SERVER's, the strip is the slots' ─────────────────────────────────────
 * `completedCount`, `prescribedCount`, `missedCount`, `compliancePct` and `tonnageKg` are the
 * processor's own figures and are printed as they come; recounting them from the slots would be a
 * second implementation that disagrees on the first status this build does not know. The strip and
 * the records ARE read off the slots, because the response carries no per-cycle figures.
 *
 * ── A figure the wire left out is not drawn ──────────────────────────────────────────────────
 * `compliancePct` is `null` before anything was due, and a tile reading "0%" there would say the
 * client failed a plan that has not started.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesTrainingDtosResponsesExecutionSummaryResponse as ExecutionSummary,
  TrainWithMeModulesTrainingDtosResponsesExecutionSummarySlot as ExecutionSlot,
} from '~/shared/api/generated/model';
import { calendarDayOf } from '~/entities/calendar-event';

import type { PlanGhost } from './monthCalendar';

/** One slot on the week strip. `ahead` is not yet due; `other` was withdrawn or is unknown. */
export type SlotMark = 'done' | 'missed' | 'ahead' | 'other';

export interface ExecutionWeek {
  /** One-based, the cycle's own number. */
  readonly number: number;
  readonly marks: readonly SlotMark[];
  readonly isCurrent: boolean;
}

export interface PlanExecution {
  readonly done: number;
  readonly prescribed: number;
  readonly missed: number;
  /** Rounded percent, or `null` where nothing was due yet. */
  readonly compliancePct: number | null;
  readonly tonnageKg: number;
  /** Slots that set a personal record. */
  readonly records: number;
  /** Sessions still ahead — prescribed minus finished minus missed, never below zero. */
  readonly left: number;
  readonly weeks: readonly ExecutionWeek[];
  /** One-based cycle the client is in, or `null` where the plan has not started or has ended. */
  readonly currentWeek: number | null;
  readonly weekTotal: number;
}

const DONE = new Set(['Done', 'DoneLate']);
const MISSED = new Set(['Missed']);
const AHEAD = new Set(['Upcoming', 'InProgress']);

function markOf(slot: ExecutionSlot): SlotMark {
  const status = slot.status ?? '';
  if (DONE.has(status)) return 'done';
  if (MISSED.has(status)) return 'missed';
  if (AHEAD.has(status)) return 'ahead';
  return 'other';
}

function count(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function planExecution(summary: ExecutionSummary): PlanExecution {
  const weeks = (summary.weeks ?? []).map((week, index) => ({
    number: typeof week.cycleIndex === 'number' ? week.cycleIndex + 1 : index + 1,
    marks: (week.slots ?? []).map(markOf),
    isCurrent: typeof summary.currentCycleIndex === 'number' && week.cycleIndex === summary.currentCycleIndex,
  }));
  const records = (summary.weeks ?? [])
    .flatMap((week) => week.slots ?? [])
    .filter((slot) => slot.hasPr === true).length;
  const done = count(summary.completedCount);
  const prescribed = count(summary.prescribedCount);
  const missed = count(summary.missedCount);

  return {
    done,
    prescribed,
    missed,
    compliancePct:
      typeof summary.compliancePct === 'number' ? Math.round(summary.compliancePct) : null,
    tonnageKg: count(summary.tonnageKg),
    records,
    left: Math.max(0, prescribed - done - missed),
    weeks,
    currentWeek:
      typeof summary.currentCycleIndex === 'number' ? summary.currentCycleIndex + 1 : null,
    weekTotal: typeof summary.cycleTotal === 'number' ? summary.cycleTotal : weeks.length,
  };
}

/**
 * The plan's days nobody has booked yet, for the empty calendar's month: every slot still ahead
 * that names a civil day. A self-paced plan's slots name none (self-paced-plan R15), so it yields
 * nothing and the month is drawn without ghosts rather than with invented ones.
 */
export function unbookedPlanDays(summary: ExecutionSummary): readonly PlanGhost[] {
  const ghosts: PlanGhost[] = [];
  for (const slot of (summary.weeks ?? []).flatMap((week) => week.slots ?? [])) {
    if (markOf(slot) !== 'ahead' || typeof slot.calendarEventId === 'number') continue;
    const day = calendarDayOf(slot.moment?.date ?? slot.displayDate ?? null);
    if (day === null) continue;
    ghosts.push({ day, title: slot.dayName?.trim() ?? slot.sessionName?.trim() ?? '' });
  }
  return ghosts;
}
